import React from "react";
import { Facebook, Twitter, Instagram, Linkedin, MapPin, Mail, Phone, ExternalLink } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function QuickLinksConsumer() {
  const {
    menuItems,
    loading,
    error
  } = useMenu();
  if (loading) {
    return <div data-section-id="24245" className="flex flex-col gap-2">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-24 bg-muted/20 animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  if (error || !menuItems || menuItems.length === 0) {
    return <div className="text-muted-foreground text-xs">Menu unavailable</div>;
  }
  return <nav className="flex flex-col gap-2">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center gap-2 group w-fit" data-index={i}>
          <span className="w-1 h-1 rounded-full bg-muted-foreground/50 group-hover:bg-primary transition-colors" />
          {item.label}
        </Link>)}
    </nav>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" className="dark bg-background text-foreground border-t border-border/10">
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">

          {}
          <div className="space-y-6">
            <Link to="/HomePage" className="block w-fit">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1604077/img/quantificial_logo.png" alt="Quantificial Logo" className="h-8 w-auto opacity-100 hover:opacity-90 transition-opacity brightness-0 invert" />
            </Link>

            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs font-medium">
              AI-first transformation partner for private-equity firms and enterprise CIOs. Turning data and operations into measurable performance.
            </p>

            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <MapPin className="size-4 text-primary shrink-0" />
                <span>Calgary, AB</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="size-4 text-primary shrink-0" />
                <Link to="mailto:questions@quantificial.ai" className="hover:text-foreground transition-colors">
                  questions@quantificial.ai
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="size-4 text-primary shrink-0" />
                <Link to="#" className="hover:text-foreground transition-colors">+1 (403) 995 7272</Link>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-bold text-foreground tracking-tight text-base">Quick Links</h3>
            <MenuProvider menu_id="16" className="w-full">
              <QuickLinksConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-bold text-foreground tracking-tight text-base">Industries</h3>
            <ul className="space-y-2">
              {[{
              name: "Financial Services",
              url: "/IndustriesPage"
            }, {
              name: "Healthcare & Life Sciences",
              url: "/IndustriesPage"
            }, {
              name: "Retail & Consumer",
              url: "/IndustriesPage"
            }, {
              name: "Technology & Media",
              url: "/IndustriesPage"
            }, {
              name: "Private Equity",
              url: "/IndustriesPage"
            }].map((source, i) => <li key={i} data-index={i}>
                  <Link to={source.url} className="text-muted-foreground hover:text-primary transition-colors text-sm flex items-center justify-between group w-fit">
                    {source.name}
                    <ExternalLink className="size-3 ml-2 opacity-0 group-hover:opacity-50 transition-opacity" />
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="font-bold text-foreground tracking-tight text-base">Direct Inquiries</h3>

            <div className="bg-card/30 border border-border/20 rounded-lg p-5 space-y-4 backdrop-blur-md">
              <div className="space-y-2">
                <Link to="mailto:questions@quantificial.ai" className="text-lg md:text-xl font-bold text-foreground hover:text-primary transition-all block tracking-tight">
                  questions@quantificial.ai
                </Link>
                <div className="h-1 w-24 bg-gradient-to-r from-[hsl(200,80%,55%)] to-[hsl(140,70%,55%)] rounded-full" />
              </div>
              
              <p className="text-xs text-muted-foreground font-medium leading-tight">
                Connect with our transformation experts to discuss your enterprise strategy.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Connect</h4>
              <div className="flex gap-2">
                {[{
                icon: Linkedin,
                label: "LinkedIn",
                href: "#"
              }, {
                icon: Twitter,
                label: "Twitter",
                href: "#"
              }].map((social, i) => <Button key={i} variant="outline" size="icon" className="size-9 rounded-md border-border/50 bg-background/50 hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-all" asChild data-index={i}>
                    <Link to={social.href} aria-label={social.label}>
                      <social.icon className="size-4" />
                    </Link>
                  </Button>)}
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-border/10" />

        {}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
          <p>&copy; {currentYear} Quantificial. All rights reserved.</p>

          <div className="flex items-center gap-1 flex-wrap justify-center">
            <Link to="#" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <span className="text-muted-foreground/30 mx-2">•</span>
            <Link to="#" className="hover:text-foreground transition-colors">Terms of Service</Link>
            <span className="text-muted-foreground/30 mx-2">•</span>
            <Link to="#" className="hover:text-foreground transition-colors">Cookie Policy</Link>
            <span className="text-muted-foreground/30 mx-2">•</span>
            <Link to="#" className="hover:text-foreground transition-colors">Sitemap</Link>
          </div>
        </div>
      </div>
    </section>;
}